// Settings Types for Hour Configurations

export interface FeatureHourConfig {
  id: string
  label: string
  defaultHours: number
  description?: string
}

export interface SectionHourConfig {
  sectionId: string
  sectionLabel: string
  features: FeatureHourConfig[]
}

// KYC Feature Hours
export interface KycHourSettings {
  baseSetup: number
  auDocumentPassport: number
  auDocumentDriversLicense: number
  auDocumentMedicare: number
  auDocumentForeignPassport: number
  auDocumentBirthCertificate: number
  auDocumentProofOfAge: number
  nzDocumentPassport: number
  nzDocumentDriversLicense: number
  nzDocumentBirthCertificate: number
  consentSources: number
  dvsSetup: number
  vevoIntegration: number
  internationalKycPerCountry: number
  customRuleset: number
  vendorIntegration: number
  // New — previously untracked options
  perAdditionalRuleset: number
  manualReviewSetup: number
  cascadeSetup: number
  perDataSource: number
  agingSetup: number
}

// AML Feature Hours
export interface AmlHourSettings {
  baseSetup: number
  standardLevel: number
  enhancedLevel: number
  pepLevel1: number
  pepLevel2: number
  pepLevel3: number
  pepLevel4: number
  ongoingMonitoring: number
  customSanctions: number
  customWatchlists: number
  vendorIntegration: number
  // New — previously untracked options
  sanctionsScreening: number
  adverseMediaScreening: number
  additionalWatchlists: number
  batchProcessing: number
  vendorSpecificSetup: number
  agingSetup: number
}

// Biometrics/IDV Feature Hours
export interface BiometricsHourSettings {
  baseSetup: number
  documentReview: number
  documentBiometricsReview: number
  perDocumentType: number
  hostedOneSdk: number
  embeddedOneSdk: number
  otherFrontend: number
  webPlatform: number
  inAppPlatform: number
  bothPlatforms: number
  rebrandingHostedOneSDK: number
  rebrandingDaon: number
  extendedBranding: number
  customRedirectPage: number
  customConsents: number
  vendorIntegration: number
}

// KYB Feature Hours
export interface KybHourSettings {
  baseSetup: number
  kybCheck: number
  amlStandard: number
  amlEnhanced: number
  asicInfo: number
  ubo: number
  creditScore: number
  paymentPredictor: number
  adverseInfo: number
  roleDirectors: number
  roleShareholders: number
  roleBeneficialOwners: number
  perEntityType: number
  internationalPerCountry: number
  // New — previously untracked options
  trustAnalyserSetup: number
  onboardingFormSetup: number
  onboardingFormBranding: number
  amlEntityScreening: number
  uboWorkflowSetup: number
  agingSetup: number
}

// Recipes & Rules Hours
export interface RecipesRulesHourSettings {
  perRecipe: number
  customDuplicateRules: number
  customBlocklistRules: number
}

// Workflows Feature Hours
export interface WorkflowsHourSettings {
  perBuilderWorkflow: number
  perComplexWorkflow: number
  perWorkflowStep: number
  perRiskBranch: number
  perCascadeSource: number
}

// Risk Configuration Feature Hours
export interface RiskHourSettings {
  baseSetup: number
  perCustomRiskFactor: number
  scoringMatrixSetup: number
  individualModelSetup: number
}

// Fraud & Transaction Monitoring Feature Hours
export interface FraudTxmHourSettings {
  fraudSetup: number
  transactionMonitoringSetup: number
}

// General Setup Hours
export interface GeneralHourSettings {
  projectSetup: number
  parentChildConfig: number
  testing: number
  training: number
  documentation: number
  // New — previously untracked options
  integrationApiSetup: number
  integrationPortalSetup: number
  ssoSetup: number
  aiFeatures: number
  perCustomWorkItem: number
}

// Jira Ticket Prompts
export interface JiraPromptSettings {
  v1Prompt: string
  v2Prompt: string
}

export const DEFAULT_JIRA_PROMPTS: JiraPromptSettings = {
  v1Prompt: '',
  v2Prompt: '',
}

// Combined Settings
export interface HourSettings {
  kyc: KycHourSettings
  aml: AmlHourSettings
  biometrics: BiometricsHourSettings
  kyb: KybHourSettings
  recipesRules: RecipesRulesHourSettings
  workflows: WorkflowsHourSettings
  risk: RiskHourSettings
  fraudTxm: FraudTxmHourSettings
  general: GeneralHourSettings
}

// Default hour values
export const DEFAULT_KYC_HOURS: KycHourSettings = {
  baseSetup: 2,
  auDocumentPassport: 0.5,
  auDocumentDriversLicense: 0.5,
  auDocumentMedicare: 0.5,
  auDocumentForeignPassport: 1,
  auDocumentBirthCertificate: 0.5,
  auDocumentProofOfAge: 0.5,
  nzDocumentPassport: 0.5,
  nzDocumentDriversLicense: 0.5,
  nzDocumentBirthCertificate: 0.5,
  consentSources: 1,
  dvsSetup: 2,
  vevoIntegration: 4,
  internationalKycPerCountry: 2,
  customRuleset: 4,
  vendorIntegration: 2,
  perAdditionalRuleset: 2,
  manualReviewSetup: 2,
  cascadeSetup: 3,
  perDataSource: 1,
  agingSetup: 2,
}

export const DEFAULT_AML_HOURS: AmlHourSettings = {
  baseSetup: 2,
  standardLevel: 1,
  enhancedLevel: 2,
  pepLevel1: 0.5,
  pepLevel2: 0.5,
  pepLevel3: 1,
  pepLevel4: 1,
  ongoingMonitoring: 4,
  customSanctions: 2,
  customWatchlists: 2,
  vendorIntegration: 2,
  sanctionsScreening: 2,
  adverseMediaScreening: 2,
  additionalWatchlists: 1,
  batchProcessing: 3,
  vendorSpecificSetup: 2,
  agingSetup: 2,
}

export const DEFAULT_BIOMETRICS_HOURS: BiometricsHourSettings = {
  baseSetup: 4,
  documentReview: 2,
  documentBiometricsReview: 4,
  perDocumentType: 1,
  hostedOneSdk: 4,
  embeddedOneSdk: 8,
  otherFrontend: 12,
  webPlatform: 2,
  inAppPlatform: 4,
  bothPlatforms: 6,
  rebrandingHostedOneSDK: 4,
  rebrandingDaon: 6,
  extendedBranding: 8,
  customRedirectPage: 4,
  customConsents: 2,
  vendorIntegration: 4,
}

export const DEFAULT_KYB_HOURS: KybHourSettings = {
  baseSetup: 4,
  kybCheck: 2,
  amlStandard: 1,
  amlEnhanced: 2,
  asicInfo: 1,
  ubo: 3,
  creditScore: 2,
  paymentPredictor: 2,
  adverseInfo: 2,
  roleDirectors: 1,
  roleShareholders: 1,
  roleBeneficialOwners: 2,
  perEntityType: 1,
  internationalPerCountry: 3,
  trustAnalyserSetup: 3,
  onboardingFormSetup: 4,
  onboardingFormBranding: 2,
  amlEntityScreening: 2,
  uboWorkflowSetup: 2,
  agingSetup: 2,
}

export const DEFAULT_RECIPES_RULES_HOURS: RecipesRulesHourSettings = {
  perRecipe: 2,
  customDuplicateRules: 3,
  customBlocklistRules: 3,
}

export const DEFAULT_WORKFLOWS_HOURS: WorkflowsHourSettings = {
  perBuilderWorkflow: 4,
  perComplexWorkflow: 6,
  perWorkflowStep: 1,
  perRiskBranch: 2,
  perCascadeSource: 0.5,
}

export const DEFAULT_RISK_HOURS: RiskHourSettings = {
  baseSetup: 2,
  perCustomRiskFactor: 2,
  scoringMatrixSetup: 3,
  individualModelSetup: 2,
}

export const DEFAULT_FRAUD_TXM_HOURS: FraudTxmHourSettings = {
  fraudSetup: 4,
  transactionMonitoringSetup: 6,
}

export const DEFAULT_GENERAL_HOURS: GeneralHourSettings = {
  projectSetup: 4,
  parentChildConfig: 2,
  testing: 8,
  training: 4,
  documentation: 2,
  integrationApiSetup: 4,
  integrationPortalSetup: 2,
  ssoSetup: 3,
  aiFeatures: 4,
  perCustomWorkItem: 4,
}

export const DEFAULT_HOUR_SETTINGS: HourSettings = {
  kyc: DEFAULT_KYC_HOURS,
  aml: DEFAULT_AML_HOURS,
  biometrics: DEFAULT_BIOMETRICS_HOURS,
  kyb: DEFAULT_KYB_HOURS,
  recipesRules: DEFAULT_RECIPES_RULES_HOURS,
  workflows: DEFAULT_WORKFLOWS_HOURS,
  risk: DEFAULT_RISK_HOURS,
  fraudTxm: DEFAULT_FRAUD_TXM_HOURS,
  general: DEFAULT_GENERAL_HOURS,
}
